<script setup lang="ts">
import type { Ride } from '../../types/ride';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

const props = defineProps<{
  ride: Ride;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

function formatDate(dateStr: string): string {
  return format(parseISO(dateStr), 'dd. MMMM yyyy', { locale: de });
}

function handleEmailClick(email: string) {
  window.location.href = `mailto:${email}`;
}

function handlePhoneClick(phone: string) {
  window.location.href = `tel:${phone}`;
}
</script>

<template>
  <div class="modal-overlay" @click="emit('close')">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <div class="ride-type" :class="ride.ride_type">
          {{ ride.ride_type === 'offer' ? 'Mitfahrt angeboten' : 'Mitfahrer gesucht' }}
        </div>
        <div class="ride-date">{{ formatDate(ride.date) }}</div>
      </div>
      
      <div class="ride-details">
        <div class="detail-section">
          <h4>Route</h4>
          <div class="route-info">
            <div class="route-point">
              <span class="label">Von:</span>
              <span class="value">{{ ride.from }}</span>
            </div>
            <div v-if="ride.via" class="route-point">
              <span class="label">Über:</span>
              <span class="value">{{ ride.via }}</span>
            </div>
            <div class="route-point">
              <span class="label">Nach:</span>
              <span class="value">{{ ride.to }}</span>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <h4>Kontakt</h4>
          <div class="contact-info">
            <div class="contact-name">{{ ride.name }}</div>
            <a 
              class="contact-link"
              :href="`mailto:${ride.email}`"
              @click.stop="handleEmailClick(ride.email)"
            >
              {{ ride.email }}
            </a>
            <a 
              class="contact-link"
              :href="`tel:${ride.telefon}`"
              @click.stop="handlePhoneClick(ride.telefon)"
            >
              {{ ride.telefon }}
            </a>
          </div>
        </div>
      </div>

      <button class="close-button" @click="emit('close')">Schließen</button>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}

.modal-header {
  margin-bottom: 1.5rem;
  text-align: center;
}

.ride-type {
  color: #960040;
  font-size: 1.2rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.ride-date {
  color: #666;
  font-size: 0.9rem;
}

.ride-details {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.detail-section {
  background: #f8f8f8;
  padding: 1rem;
  border-radius: 6px;
}

h4 {
  color: #960040;
  margin-bottom: 0.75rem;
  font-size: 1rem;
}

.route-info, .contact-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.route-point {
  display: flex;
  gap: 0.5rem;
}

.label {
  color: #666;
  min-width: 45px;
}

.value {
  color: #960040;
  font-weight: 500;
}

.contact-name {
  color: #960040;
  font-weight: 500;
}

.contact-link {
  color: #666;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.contact-link:hover {
  color: #960040;
}

.close-button {
  margin-top: 1.5rem;
  width: 100%;
  padding: 0.8rem;
  background: #960040;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.close-button:hover {
  background: #7d0035;
}
</style>